<template>
  <div class="col-12">
    <div class="card no-hover pt-2 bg-blurry">
      <div class="table-responsive-md overflow-y-hidden">
        <table class="table table-hover align-middle position-relative blur mb-0" itemscope itemtype="https://schema.org/Table">
          <thead>
          <tr>
            <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Vlucht' : 'Flight'}}</th>
            <th v-if="!liveOnly" scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Datum' : 'Date'}}</th>
            <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Van' : 'From'}}</th>
            <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Naar' : 'To'}}</th>
            <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Duur' : 'Duration'}}</th>
            <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">Status</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
            <tr @click="openLink((isDutch ? '/nl/vluchten/' : '/flights/') + flight.flight_number.toLowerCase())"
                v-for="flight in flights.slice(0, limitAircrafts)" :key="flight.flight_number">
              <th scope="row">
                <NuxtLink
                    class="badge rounded-pill small border mb-0 bg-blurry inherit"
                    :title="isDutch ?
                    ('Vlucht ' + flight.flight_number + ' van ' + flight.origin.city.name_nl + ' naar ' + flight.destination.city.name_nl) :
                    ('Flight ' + flight.flight_number + ' from ' + flight.origin.city.name + ' to ' + flight.destination.city.name)"
                    :to="{ name: isDutch ? 'nl-vluchten-slug' : 'flights-slug', params: { slug: flight.flight_number.toLowerCase() } }">
                  {{ flight.flight_number }}
                </NuxtLink>
              </th>
              <th v-if="!liveOnly">
                <p class="mb-0">{{ formatDate(flight.scheduled_departure) }}</p>
                <p class="mb-0 small opacity-75">{{ formatTime(flight.scheduled_departure) }}</p>
              </th>
              <th>
                <p class="mb-0">{{ flight.origin.iata}}</p>
                <p v-if="isDutch" class="small opacity-75 mb-0">
                  {{ flight.origin.city.name_nl}}
                </p>
                <p v-else class="small opacity-75 mb-0">
                  {{ flight.origin.city.name}}
                </p>
              </th>
              <th>
                <p class="mb-0">{{ flight.destination.iata}}</p>
                <p v-if="isDutch" class="small opacity-75 mb-0">{{ flight.destination.city.name_nl}}</p>
                <p v-else class="small opacity-75 mb-0">{{ flight.destination.city.name}}</p>
              </th>
              <th>
                <p class="mb-0">{{ createFormattedTime(flight.flight_duration) }}</p>
                <p class="small opacity-75 mb-0">{{ Intl.NumberFormat().format(flight.flight_distance) }} km</p>
              </th>
              <th>
                <span class="badge rounded-pill small border mb-0 bg-blurry inherit" :class="sanitizeTitle('status-'+ flight.status)">
                  <span class="live-animation" v-if="flight.status === 'Live'">●</span>
                  {{ translateStatus(flight.status) }}
                </span>
              </th>
              <th class="text-end">
                <button type="button" class="btn px-2 py-1 small fw-bold btn-primary pe-none">
                  {{ isDutch ? 'Bekijk' : 'View' }}
                </button>
              </th>
            </tr>
          </tbody>
          <tfoot class="blur" v-if="!authStore.user && !showLoadMore">
            <tr v-for="n in 4">
              <th>
                <span class="badge rounded-pill border mb-0 bg-blurry">ABC-DEFG</span>
              </th>
              <th>
                Boeing 787-123
              </th>
              <th>
                10
              </th>
              <th>
                10
              </th>
              <th>
                2021-12-31
              </th>
              <th>
                2021-12-31
              </th>
              <th class="text-end">
                <span class="btn px-2 py-1 small fw-bold btn-primary grayscale">
                  {{ isDutch ? 'Bekijk' : 'View' }}
                </span>
              </th>
            </tr>
          </tfoot>
        </table>
        <div v-if="!authStore.user && !showLoadMore" class="table-disclaimer">
          <div class="bg-transparent text-center">
            <NuxtImg
                class="mb-2"
                alt="Pilot Emoji"
                width="30"
                height="30"
                loading="lazy"
                src="img/pilot-emoji.png"
            />
            <p v-if="isDutch" class="h3 text-primary">Meer zien? Meld je aan!</p>
            <p v-else class="h3 text-primary">Get Full Access</p>

            <p v-if="isDutch" class="mb-0">Voor volledige toegang, maak een <b>gratis account</b> of <b>log in</b>.</p>
            <p v-else class="mb-0">For full access, create a <b>free account</b> or <b>log in</b>.</p>

            <p v-if="isDutch" class="mb-0">Klik op <b>Account</b> in het menu om in te loggen.</p>
            <p v-else class="mb-0">Tap <b>Account</b> in the menu to get started.</p>
          </div>
        </div>

        <div v-if="!showLoadMore" class="pt-3 pb-3 ps-3">
          <p v-if="isDutch" class="mb-0 mt-0 small opacity-75">
            Vluchtdata getoond in lokale tijd; statusupdates zijn mogelijk niet real-time.
          </p>
          <p v-else class="mb-0 mt-0 small opacity-75">
            Flight data is displayed in local time; status updates may not be real-time.
          </p>
        </div>

        <div class="text-center mb-3 pt-3 border-top" v-if="(authStore.user || showLoadMore) && limitAircrafts < flights.length">
          <button @click="increaseLimit" role="button" v-if="isDutch" class="badge bg-primary-darken border rounded-pill mb-0">Meer vluchten laden</button>
          <button @click="increaseLimit" role="button" v-else class="badge bg-primary-darken border rounded-pill mb-0">Load more</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";

import {useAuthStore} from "~/store/auth.js";
import {toLowerCase} from "valibot";

interface Flight {
  flight_number: string,
  status: string,
  origin: object,
  destination: object,
  scheduled_departure: string,
  is_landed: boolean;
  is_live: boolean;
}

interface Props {
  flights: Flight[],
  showLoadMore: boolean,
  limit?: number,
  liveOnly?: boolean
}

const authStore = useAuthStore();
const isDutch = inject('isDutch');

const props = defineProps<Props>();

const showLoadMore = props.showLoadMore;

const limitAircrafts =  ref(props.limit ? props.limit : 5);

const increaseLimit = () => {
  limitAircrafts.value += 25;
};

const createFormattedTime = (durationInMinutes: string) => computed(() => {
  let duration = moment.duration(durationInMinutes, 'minutes');
  return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
});

function formatDate(date: string | null) {
  if (date === null) {
    return '-';
  }

  const flight = moment(date);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const tomorrow = moment().add(1, 'days').startOf('day');

  if (flight.isSame(today, 'day')) {
    return isDutch ? 'Vandaag' : 'Today';
  } else if (flight.isSame(yesterday, 'day')) {
    return isDutch ? 'Gisteren' : 'Yesterday';
  } else if (flight.isSame(tomorrow, 'day')) {
    return isDutch ? 'Morgen' : 'Tomorrow';
  }

  return moment(date).format('D MMM.');
}

function formatTime(date: string | null) {
  if (date === null) {
    return '-';
  }

  return moment(date).format('HH:mm');
}

function sanitizeTitle(title: string) {
  let slug = "";
  const titleLower = title.toLowerCase();
  slug = titleLower.replace(/\s*$/g, '');
  slug = slug.replace(/\s+/g, '-');
  return slug;
}

function translateStatus(status: string) {
  if (!isDutch) {
    if (status === 'Live') {
      return 'In Air';
    }
    return status;
  }

  if (status === 'Scheduled') {
    return 'Gepland';
  } else if (status === 'Landed') {
    return 'Geland';
  } else if (status === 'Live') {
    return 'In de lucht';
  } else if (status === 'Diverted') {
    return 'Omgeleid';
  } else if (status === 'Canceled') {
    return 'Geannuleerd';
  }

  return status
}

function openLink(url: string | URL | undefined) {
  setTimeout(() => {
    window.open(url, '_self');
  })
}
</script>